import { t } from '../locales/i18nManager';

export type RouteInfo = {
  key: string;
  label?: string;
  route: string;
  routesGroup?: string;
  routeReload?: string;
  routeNoReload?: string;
};

export const routes: {
  [key: string]: RouteInfo;
} = {
  welcome: {
    key: 'welcome',
    route: '/welcome',
  },
  login: {
    key: 'login',
    label: t('menu.login'),
    route: '/login',
  },
  register: {
    key: 'register',
    label: t('menu.register'),
    route: '/register',
  },
  onboarding: {
    key: 'onboarding',
    route: '/onboarding',
  },
  onboardingQuestionnaire: {
    key: 'onboardingQuestionnaire',
    route: '/onboarding-questionnaire',
  },
  medicalProfile: {
    key: 'medicalProfile',
    route: '/medical-profile',
  },
  passwordResetSendEmail: {
    key: 'passwordResetSendEmail',
    label: t('menu.passwordReset'),
    route: '/password-reset',
  },
  passwordReset: {
    key: 'passwordReset',
    label: t('menu.passwordReset'),
    route: '/password/reset/:uid/:token',
  },
  home: {
    key: 'home',
    label: t('menu.home'),
    route: '/',
    routesGroup: 'home',
  },
  messagingHome: {
    key: 'messagingHome',
    label: t('menu.messagingHome'),
    route: '/conversations',
    routeReload: '/conversations?reload=true',
    routeNoReload: '/conversations?reload=false',
    routesGroup: 'messaging',
  },
  messagingHowto: {
    key: 'messagingHowto',
    route: '/conversations-explainer',
    routesGroup: 'messaging',
  },
  messagingTeam: {
    key: 'messagingTeam',
    route: '/conversations-team',
    routesGroup: 'messaging',
  },
  conversation: {
    key: 'conversation',
    label: t('menu.conversation'),
    route: '/conversations/:conversationId',
    routeReload: '/conversations/:conversationId?reload=true',
    routesGroup: 'messaging',
  },
  patientDocuments: {
    key: 'patientDocuments',
    route: '/conversations/:conversationId/documents',
    routesGroup: 'messaging',
  },
  caregiverDossier: {
    key: 'caregiverDossier',
    route: '/conversations/:conversationId/dossier',
    routesGroup: 'messaging',
  },
  patientConversationsHistory: {
    key: 'patientConversationsHistory',
    route: '/conversations/:conversationId/history',
    routesGroup: 'messaging',
  },
  patientHistoricConversation: {
    key: 'patientConversationHistory',
    route: '/conversations/:conversationId/history/:historicConversation/',
    routesGroup: 'messaging',
  },
  newChat: {
    key: 'newChat',
    label: t('menu.newChat'),
    route: '/new-conversation',
    routesGroup: 'messaging',
  },
  newChatCheckup: {
    key: 'newChatCheckup',
    label: t('menu.newChat'),
    route: '/new-conversation?conversationType=checkup',
    routesGroup: 'messaging',
  },
  info: {
    key: 'info',
    label: t('menu.info'),
    route: '/info',
    routesGroup: 'info',
  },
  // One category of the categorised Infos page (an area or a theme)
  infoCategory: {
    key: 'infoCategory',
    route: '/info/:slug',
    routesGroup: 'info',
  },
  article: {
    key: 'article',
    route: '/article/:index',
  },
  faq: {
    key: 'faq',
    route: '/faq',
  },
  symptomTracking: {
    key: 'symptomTracking',
    label: t('menu.tracking'),
    route: '/track',
    routesGroup: 'tracking',
  },
  settings: {
    key: 'settings',
    label: t('menu.settings'),
    route: '/settings',
  },
  logout: {
    key: 'logout',
    label: t('menu.logout'),
    route: '/logout',
  },
  designSystem: {
    key: 'designSystem',
    label: 'Design system',
    route: '/design-system',
  },
  notFound: {
    key: 'notFound',
    label: t('menu.notFound'),
    route: '*',
  },
  unauthorized: {
    key: 'unauthorized',
    label: t('menu.unauthorized'),
    route: '*',
  },
};

/** Routes that are not valid post-login redirect targets */
/**
 * Routes reachable without being signed in.
 *
 * `designSystem` is here deliberately: it renders tokens and empty components
 * and reads no data of any kind, and the people most likely to want it — a
 * designer, someone checking a colour against the brandbook — are exactly the
 * people least likely to have an account. Remove it from this list to put the
 * style guide behind login.
 */
export const AUTH_ROUTES = [
  routes.welcome.route,
  routes.designSystem.route,
  routes.login.route,
  routes.register.route,
  routes.onboarding.route,
  routes.onboardingQuestionnaire.route,
  routes.passwordResetSendEmail.route,
  routes.passwordReset.route,
  routes.notFound.route,
  routes.unauthorized.route,
];

export type RedirectState = { from: { pathname: string; search?: string } };

/** State passed to Welcome/Login; fromLogout means the next user should land on home. */
export type AuthLocationState = RedirectState & { fromLogout?: boolean };

/**
 * Returns a safe internal path to redirect to after login, or undefined if none.
 * Prevents open redirect and avoids redirecting back to auth pages.
 */
export function getRedirectPath(location: { state?: unknown }): string | undefined {
  const from = (location.state as { from?: { pathname?: string; search?: string } } | undefined)?.from;
  if (!from?.pathname || from.pathname.startsWith('//')) return undefined;
  const path = from.pathname + (from.search ?? '');
  const isAuthRoute = AUTH_ROUTES.some(r => {
    const prefix = r.split('/:')[0];
    return from.pathname === prefix || from.pathname!.startsWith(prefix + '/');
  });
  return isAuthRoute ? undefined : path;
}
